import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { FormProvider, useForm } from 'react-hook-form'
import { Alert, Box, Dialog, DialogContent, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import Grid from '@mui/material/Grid'
import { get, noop } from 'lodash'
import { useUpdateBillingAddressMutation } from 'api/accounts/updateBillingAddress'
import HHDialogTitle from '../../../common/HHDialogTitle'
import T from '../../../../T'
import HHDialogActions from '../../../common/HHDialogActions'
import AddressFormFields from './AddressFormFields'
import { selectAddress } from '../../../../data/address/addressSelectors'
import { combineAddressInfo, splitAddressLine1 } from '../../../../utils/helper'
import MapPreview from './MapPreview'
import AddressDetails from '../common/AddressDetails'
import CancelButton from '../../../buttons/CancelButton'
import SaveButton from '../../../buttons/SaveButton'

const initialLatitude = '32.77321'
const initialLongitude = '-117.15920'

const defaultValues = {
  addressName: '',
  line1: '',
  unitNumber: '',
  city: '',
  state: '',
  country: '',
  zipCode: '',
}

const addressObjToOptionFormat = addressObj => {
  const { line1, city, state, zipCode, country, latitude, longitude } = addressObj
  const splittedLine1 = splitAddressLine1(line1)
  const streetNumber = splittedLine1.number
  const { streetName } = splittedLine1
  return {
    ...addressObj,
    streetNumber,
    streetName,
    city,
    stateShortCode: state,
    zipCode,
    country,
    center: {
      longitude,
      latitude,
    },
    text: combineAddressInfo(addressObj),
  }
}

const EditBillingLocationModal = ({ open = false, customerDetails, onClose = noop, onSave = noop }) => {
  const methods = useForm({ defaultValues })
  const { reset, handleSubmit, watch } = methods
  const watchAllFields = watch()
  const billingAddress = get(customerDetails, 'billingAddress', {})
  const attention = get(billingAddress, 'attention')
  const { city, state, latitude, longitude, line1, zipCode, unitNumber } = selectAddress(billingAddress)
  const theme = useTheme()
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('lg'))
  const isTablet = useMediaQuery(theme.breakpoints.only('md'))
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isXs = useMediaQuery(theme.breakpoints.down('sm'))
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const isTabletOrDesktop = useMemo(() => isTablet || isDesktop, [isTablet, isDesktop])

  const [defaultCoords, setDefaultCoords] = useState({
    defaultLatitude: initialLatitude,
    defaultLongitude: initialLongitude,
  })
  const [currentCoords, setCurrentCoords] = useState({
    currentLatitude: initialLatitude,
    currentLongitude: initialLongitude,
  })
  const [updateBillingAddress, { isLoading }] = useUpdateBillingAddressMutation()

  const { currentLatitude, currentLongitude } = currentCoords
  const { defaultLatitude, defaultLongitude } = defaultCoords

  const handleAddressChange = (e, addressObj) => {
    const { latitude: newLatitude, longitude: newLongitude } = addressObj
    setDefaultCoords({
      defaultLatitude: newLatitude,
      defaultLongitude: newLongitude,
    })
    setCurrentCoords({
      currentLatitude: newLatitude,
      currentLongitude: newLongitude,
    })
  }

  const handleSave = data => {
    // eslint-disable-next-line no-shadow
    const { line1, unitNumber, city, state, country, zipCode, addressName } = data
    const accountId = get(customerDetails, 'id')

    const body = {
      attention: addressName,
      line1,
      unitNumber,
      city,
      state,
      country,
      zipCode,
      latitude: currentLatitude,
      longitude: currentLongitude,
    }
    updateBillingAddress({ accountId, body })
      .unwrap()
      .then(() => {
        onSave()
      })
  }

  useEffect(() => {
    if (open) {
      reset({
        addressName: attention,
        city,
        state,
        latitude,
        longitude,
        line1,
        zipCode,
        unitNumber,
        address: addressObjToOptionFormat(billingAddress),
      })
      setDefaultCoords({
        defaultLatitude: latitude,
        defaultLongitude: longitude,
      })
      setCurrentCoords({
        currentLatitude: latitude,
        currentLongitude: longitude,
      })
    } else {
      reset(defaultValues)
    }
  }, [open])

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <HHDialogTitle sx={{ px: 3, pt: 3 }} closeIconSx={{ top: 16 }} onClose={onClose} title={T.EDIT_BILLING_LOCATION} />
      <DialogContent sx={{ px: 3 }}>
        <FormProvider {...methods}>
          <Grid container spacing={2}>
            <Grid container size={{ xs: 12, sm: 12, md: 5, lg: 6, xl: 7 }}>
              <Grid container size={12}>
                <Grid size={{ xs: 12, sm: 12, md: 'grow', lg: 'grow', xl: 'grow' }}>
                  <MapPreview
                    defaultLongitude={defaultLongitude}
                    defaultLatitude={defaultLatitude}
                    currentLatitude={currentLatitude}
                    currentLongitude={currentLongitude}
                    setCurrentCoords={setCurrentCoords}
                  />
                </Grid>
                {isDesktop && (
                  <Grid size={{ xs: 12, sm: 12, md: 'grow', lg: 'grow', xl: 'grow' }}>
                    <AddressDetails
                      spacing={2}
                      sx={{ px: 2, pt: isTabletOrMobile ? 4 : 3, pb: 2 }}
                      address={watchAllFields}
                      showAddressName
                      addressNameLabel={T.ATTENTION}
                      line1Label={T.BILLING_ADDRESS}
                    />
                  </Grid>
                )}
              </Grid>
              <Grid size={12}>
                <Alert
                  sx={{ display: 'flex', alignItems: 'center', borderColor: 'info.light', borderWidth: 1.5, borderStyle: 'solid' }}
                  severity="info"
                >
                  <Typography variant="h6" fontWeight="normal">
                    Drag the pin to reposition as needed
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 7, lg: 6, xl: 5 }}>
              <Box sx={{ mt: 2 }}>
                <AddressFormFields onAddressChange={handleAddressChange} addressNameLabel={T.ATTENTION} showAddressNameField />
              </Box>
            </Grid>
          </Grid>
        </FormProvider>
      </DialogContent>
      <HHDialogActions>
        <CancelButton onClick={onClose} />
        <SaveButton loading={isLoading} onClick={handleSubmit(handleSave)} />
      </HHDialogActions>
    </Dialog>
  )
}

EditBillingLocationModal.propTypes = {
  open: PropTypes.bool,
  customerDetails: PropTypes.object,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
}

export default EditBillingLocationModal
